import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, Clock, User, Phone, Mail, Search, Filter, Check, X, Trash2, Eye, Plus, AlertCircle, Archive, ArchiveRestore, ChevronLeft, ChevronRight } from 'lucide-react';
import 'react-day-picker/style.css';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { fr } from 'date-fns/locale/fr';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIME_SLOTS = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30", "18:00"
];

// Helper functions for backward compatibility
const getBookingServices = (booking) => {
  if (booking.services && booking.services.length > 0) {
    return booking.services;
  }
  // Old format - single service
  if (booking.serviceName) {
    return [{
      category: booking.serviceCategory,
      name: booking.serviceName,
      price: booking.servicePrice,
      duration: booking.serviceDuration
    }];
  }
  return [];
};

const getBookingServicesDisplay = (booking) => {
  const services = getBookingServices(booking);
  if (services.length === 0) return 'N/A';
  if (services.length === 1) return services[0].name;
  return `${services.length} prestations`;
};

const getBookingDuration = (booking) => {
  if (booking.totalDuration) return booking.totalDuration;
  if (booking.serviceDuration) return booking.serviceDuration;
  return 'N/A';
};

const getBookingPrice = (booking) => {
  if (booking.totalPrice) return booking.totalPrice;
  if (booking.servicePrice) return booking.servicePrice;
  return 'N/A';
};

export const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, booking: null });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  
  const [createDialog, setCreateDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [bypassClosedDays, setBypassClosedDays] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [allTimeSlots, setAllTimeSlots] = useState(TIME_SLOTS);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [pagination.page, statusFilter, showArchived]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      // Only filter archived bookings when explicitly requested
      if (showArchived) {
        params.append('archived', 'true');
      }

      const response = await api.get(`/admin/bookings?${params.toString()}`);
      setBookings(response.data.bookings);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Erreur lors du chargement des rendez-vous');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data.services);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchAvailableSlots = async (date) => {
    if (!date) return;

    setLoadingSlots(true);
    try {
      const dateStr = date.toLocaleDateString('fr-CA'); // YYYY-MM-DD en heure locale
      const url = `${API}/bookings/available-slots?date=${dateStr}&for_admin=true`;

      const response = await axios.get(url);

      setAllTimeSlots(response.data.allSlots);
      setBookedSlots(response.data.bookedSlots);
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Erreur lors du chargement des créneaux');
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
      setSelectedTime('');
    }
  }, [selectedDate]);

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await api.patch(`/admin/bookings/${bookingId}`, { status });
      toast.success('Statut mis à jour');
      fetchBookings();
      // Rafraîchir les créneaux si une date est sélectionnée (pour la création de RDV)
      if (selectedDate) {
        fetchAvailableSlots(selectedDate);
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const archiveBooking = async (bookingId, archived) => {
    try {
      await api.patch(`/admin/bookings/${bookingId}/archive?archived=${archived}`);
      toast.success(archived ? 'Rendez-vous archivé' : 'Rendez-vous désarchivé');
      fetchBookings();
      // Rafraîchir les créneaux si une date est sélectionnée (pour la création de RDV)
      if (selectedDate) {
        fetchAvailableSlots(selectedDate);
      }
    } catch (error) {
      toast.error("Erreur lors de l'archivage");
    }
  };

  const deleteBooking = async (bookingId) => {
    try {
      await api.delete(`/admin/bookings/${bookingId}`);
      toast.success('Rendez-vous supprimé');
      setDeleteDialog({ open: false, booking: null });
      fetchBookings();
      // Rafraîchir les créneaux si une date est sélectionnée (pour la création de RDV)
      if (selectedDate) {
        fetchAvailableSlots(selectedDate);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCreateBooking = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !selectedDate || !selectedTime || !selectedService) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const serviceDetails = getSelectedServiceDetails();
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const response = await api.post(`/admin/bookings?bypass_closed_days=${bypassClosedDays}`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        serviceCategory: selectedCategory,
        serviceName: selectedService,
        servicePrice: serviceDetails?.price || '',
        serviceDuration: serviceDetails?.duration || '30min',
        date: dateStr,
        time: selectedTime,
        notes: formData.notes || null
      });
      
      toast.success('Rendez-vous créé avec succès !');
      setCreateDialog(false);

      // Sauvegarder la date sélectionnée avant reset pour rafraîchir les créneaux
      const currentDate = selectedDate;
      resetCreateForm();
      fetchBookings();

      // Rafraîchir les créneaux pour la date du RDV créé
      if (currentDate) {
        fetchAvailableSlots(currentDate);
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(errorMessage);
    }
  };

  const resetCreateForm = () => {
    setSelectedDate(undefined);
    setSelectedTime('');
    setSelectedCategory('');
    setSelectedService('');
    setBypassClosedDays(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      notes: ''
    });
  };

  const getSelectedServiceDetails = () => {
    const category = services.find(s => s.category === selectedCategory);
    if (!category) return null;
    return category.items.find(item => item.name === selectedService);
  };

  const categoryServices = selectedCategory 
    ? services.find(s => s.category === selectedCategory)?.items || []
    : [];

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      completed: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmé',
      cancelled: 'Annulé',
      completed: 'Terminé'
    };
    return <Badge className={`${styles[status]} border`}>{labels[status]}</Badge>;
  };

  const isUpcoming = (booking) => {
    const now = new Date();
    const bookingDateTime = new Date(`${booking.date}T${booking.time}`);
    const diffInHours = (bookingDateTime - now) / (1000 * 60 * 60);
    return diffInHours >= 0 && diffInHours <= 1;
  };

  // Filter only by search term (status and archivage are handled server-side)
  const filteredBookings = bookings.filter(booking => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    const services = getBookingServices(booking);
    const serviceNames = services.map(s => s.name).join(' ').toLowerCase();

    return (
      booking.firstName?.toLowerCase().includes(searchLower) ||
      booking.lastName?.toLowerCase().includes(searchLower) ||
      booking.email?.toLowerCase().includes(searchLower) ||
      serviceNames.includes(searchLower) ||
      booking.serviceName?.toLowerCase().includes(searchLower) // Backward compat
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#C9A96E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header - Mobile Responsive */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2C1810]">Gestion des Rendez-vous</h1>
            <p className="text-sm md:text-base text-[#5D4037] mt-1">Gérez et suivez tous les rendez-vous</p>
          </div>
          <Button 
            onClick={() => setCreateDialog(true)}
            className="bg-[#C9A96E] hover:bg-[#A67B5B] text-white w-full sm:w-auto"
          >
            <Plus className="mr-2" size={18} />
            Créer un RDV
          </Button>
        </div>
      </div>

      {/* Filters - Mobile Responsive */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A67B5B]" size={20} />
              <Input
                placeholder="Rechercher par nom, service, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select value={statusFilter} onValueChange={(value) => {
                setStatusFilter(value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="confirmed">Confirmé</SelectItem>
                  <SelectItem value="completed">Terminé</SelectItem>
                  <SelectItem value="cancelled">Annulé</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant={showArchived ? "default" : "outline"}
                onClick={() => {
                  setShowArchived(!showArchived);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className={showArchived ? "bg-[#C9A96E] hover:bg-[#A67B5B]" : ""}
              >
                <Archive className="mr-2" size={18} />
                {showArchived ? "Masquer archivés" : "Afficher archivés"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Desktop Table View */}
      <Card className="hidden lg:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F5F0EB]">
                <tr>
                  <th className="text-left p-4 text-[#2C1810] font-semibold">Client</th>
                  <th className="text-left p-4 text-[#2C1810] font-semibold">Service</th>
                  <th className="text-left p-4 text-[#2C1810] font-semibold">Date & Heure</th>
                  <th className="text-left p-4 text-[#2C1810] font-semibold">Statut</th>
                  <th className="text-right p-4 text-[#2C1810] font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.length > 0 ? (
                  filteredBookings.map((booking) => {
                    const upcoming = isUpcoming(booking);
                    return (
                      <tr 
                        key={booking.id} 
                        className={`border-b border-[#E8DDD4] hover:bg-[#F5F0EB]/50 ${upcoming ? 'bg-amber-50' : ''}`}
                      >
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-[#2C1810]">
                              {booking.firstName} {booking.lastName}
                              {upcoming && (
                                <AlertCircle className="inline ml-2 text-amber-600" size={16} title="RDV dans moins d'1 heure" />
                              )}
                            </p>
                            <p className="text-sm text-[#5D4037]">{booking.email}</p>
                            <p className="text-sm text-[#A67B5B]">{booking.phone}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <p className="font-medium text-[#2C1810]">{getBookingServicesDisplay(booking)}</p>
                          {getBookingServices(booking).length === 1 && (
                            <p className="text-sm text-[#5D4037]">{getBookingServices(booking)[0].category}</p>
                          )}
                          <p className="text-sm text-[#A67B5B]">{getBookingDuration(booking)}</p>
                          <p className="text-sm text-[#C9A96E]">{getBookingPrice(booking)}</p>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <CalendarIcon size={16} className="text-[#A67B5B]" />
                            <span className="text-[#2C1810]">{booking.date}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Clock size={16} className="text-[#A67B5B]" />
                            <span className="text-[#5D4037]">{booking.time}</span>
                          </div>
                        </td>
                        <td className="p-4">{getStatusBadge(booking.status)}</td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setSelectedBooking(booking)}
                              className="text-[#5D4037] hover:text-[#2C1810]"
                            >
                              <Eye size={18} />
                            </Button>
                            {booking.status === 'pending' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                className="text-green-600 hover:text-green-700"
                              >
                                <Check size={18} />
                              </Button>
                            )}
                            {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                className="text-red-600 hover:text-red-700"
                              >
                                <X size={18} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => archiveBooking(booking.id, !booking.archived)}
                              className="text-amber-600 hover:text-amber-700"
                              title={booking.archived ? "Désarchiver" : "Archiver"}
                            >
                              {booking.archived ? <ArchiveRestore size={18} /> : <Archive size={18} />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteDialog({ open: true, booking })}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={18} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-[#5D4037]">
                      Aucun rendez-vous trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Card className="mt-4">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-[#5D4037]">
                Page {pagination.page} sur {pagination.totalPages} ({pagination.total} rendez-vous)
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                >
                  <ChevronLeft size={18} />
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Suivant
                  <ChevronRight size={18} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {filteredBookings.length > 0 ? (
          filteredBookings.map((booking) => {
            const upcoming = isUpcoming(booking);
            return (
              <Card key={booking.id} className={upcoming ? 'border-amber-300 bg-amber-50/50' : ''}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-[#2C1810]">
                          {booking.firstName} {booking.lastName}
                        </p>
                        {upcoming && (
                          <AlertCircle className="text-amber-600" size={16} />
                        )}
                      </div>
                      <p className="text-sm text-[#5D4037]">{booking.email}</p>
                      <p className="text-sm text-[#A67B5B]">{booking.phone}</p>
                    </div>
                    {getStatusBadge(booking.status)}
                  </div>
                  
                  <div className="bg-[#F5F0EB] rounded-lg p-3 mb-3">
                    <p className="font-medium text-[#2C1810]">{getBookingServicesDisplay(booking)}</p>
                    {getBookingServices(booking).length === 1 && (
                      <p className="text-sm text-[#5D4037]">{getBookingServices(booking)[0].category}</p>
                    )}
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm text-[#A67B5B]">{getBookingDuration(booking)}</span>
                      <span className="font-semibold text-[#C9A96E]">{getBookingPrice(booking)}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-3 text-sm">
                    <div className="flex items-center gap-1">
                      <CalendarIcon size={14} className="text-[#A67B5B]" />
                      <span>{booking.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={14} className="text-[#A67B5B]" />
                      <span>{booking.time}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-3 border-t border-[#E8DDD4]">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedBooking(booking)}
                      className="flex-1"
                    >
                      <Eye size={14} className="mr-1" /> Voir
                    </Button>
                    {booking.status === 'pending' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                        className="text-green-600 border-green-300"
                      >
                        <Check size={14} />
                      </Button>
                    )}
                    {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                        className="text-red-600 border-red-300"
                      >
                        <X size={14} />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => archiveBooking(booking.id, !booking.archived)}
                      className="text-amber-600 border-amber-300"
                    >
                      {booking.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteDialog({ open: true, booking })}
                      className="text-red-600 border-red-300"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card>
            <CardContent className="p-8 text-center text-[#5D4037]">
              Aucun rendez-vous trouvé
            </CardContent>
          </Card>
        )}
      </div>

      {/* Pagination Mobile */}
      {pagination.totalPages > 1 && (
        <Card className="mt-4 lg:hidden">
          <CardContent className="p-4">
            <div className="flex flex-col items-center justify-between gap-4">
              <div className="text-sm text-[#5D4037]">
                Page {pagination.page} sur {pagination.totalPages} ({pagination.total} rendez-vous)
              </div>
              <div className="flex gap-2 w-full">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="flex-1"
                >
                  <ChevronLeft size={18} />
                  Préc.
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.totalPages}
                  className="flex-1"
                >
                  Suiv.
                  <ChevronRight size={18} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog - Mobile Optimized */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl md:text-2xl">Créer un rendez-vous</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-[#2C1810]">Prestation</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Catégorie *</Label>
                  <Select value={selectedCategory} onValueChange={(value) => {
                    setSelectedCategory(value);
                    setSelectedService('');
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.category}>
                          {service.category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedCategory && (
                  <div>
                    <Label>Service *</Label>
                    <Select value={selectedService} onValueChange={setSelectedService}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez" />
                      </SelectTrigger>
                      <SelectContent>
                        {categoryServices.map((item, index) => (
                          <SelectItem key={index} value={item.name}>
                            {item.name} - {item.price} ({item.duration})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-[#2C1810]">Date et heure</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Date *</Label>
                  <div className="bg-[#F5F0EB] rounded-xl p-2 sm:p-4 overflow-x-auto">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      locale={fr}
                      disabled={(date) => {
                        // Bloquer les dates passées (mais autoriser aujourd'hui)
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      className="rounded-md mx-auto"
                    />
                  </div>
                  <div className="flex items-center space-x-2 mt-2">
                    <Checkbox 
                      id="bypass" 
                      checked={bypassClosedDays}
                      onCheckedChange={setBypassClosedDays}
                    />
                    <label htmlFor="bypass" className="text-sm text-[#5D4037] cursor-pointer">
                      Autoriser les jours fermés (mardi/mercredi)
                    </label>
                  </div>
                </div>
                <div>
                  <Label>Heure *</Label>
                  {selectedDate ? (
                    loadingSlots ? (
                      <div className="bg-[#F5F0EB] rounded-xl p-8 text-center">
                        <p className="text-[#5D4037]">Chargement...</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {allTimeSlots.map((slot) => {
                          const isBooked = bookedSlots.includes(slot);
                          return (
                            <button
                              key={slot}
                              onClick={() => !isBooked && setSelectedTime(slot)}
                              disabled={isBooked}
                              className={`p-2 rounded-lg border text-sm ${
                                selectedTime === slot 
                                  ? 'bg-[#C9A96E] text-white border-[#C9A96E]' 
                                  : isBooked 
                                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed line-through' 
                                  : 'bg-white border-[#D4C4B5] hover:border-[#C9A96E]'
                              }`}
                            >
                              {slot}
                            </button>
                          );
                        })}
                      </div>
                    )
                  ) : (
                    <div className="bg-[#F5F0EB] rounded-xl p-8 text-center">
                      <p className="text-[#5D4037]">Sélectionnez d'abord une date</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-[#2C1810]">Informations client</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Prénom *</Label>
                  <Input
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="Prénom"
                  />
                </div>
                <div>
                  <Label>Nom *</Label>
                  <Input
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Nom"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@exemple.com"
                  />
                </div>
                <div>
                  <Label>Téléphone *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="06 XX XX XX XX"
                  />
                </div>
              </div>
              <div>
                <Label>Notes (optionnel)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes ou demandes spéciales..."
                  rows={3}
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  setCreateDialog(false);
                  resetCreateForm();
                }}
                className="w-full sm:w-auto"
              >
                Annuler
              </Button>
              <Button 
                onClick={handleCreateBooking}
                className="bg-[#C9A96E] hover:bg-[#A67B5B] text-white w-full sm:w-auto"
              >
                Créer le rendez-vous
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Booking Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="w-[95vw] sm:w-full max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">Détails du rendez-vous</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <User className="text-[#C9A96E]" size={20} />
                <div>
                  <p className="font-medium">{selectedBooking.firstName} {selectedBooking.lastName}</p>
                  <p className="text-sm text-[#5D4037]">{selectedBooking.email}</p>
                  <p className="text-sm text-[#5D4037]">{selectedBooking.phone}</p>
                </div>
              </div>
              <div className="p-4 bg-[#F5F0EB] rounded-lg space-y-2">
                <p className="font-semibold text-[#2C1810] mb-2">
                  {getBookingServices(selectedBooking).length > 1 ? 'Prestations réservées' : 'Prestation réservée'}
                </p>
                {getBookingServices(selectedBooking).map((service, idx) => (
                  <div key={idx} className={`${idx > 0 ? 'pt-2 mt-2 border-t border-[#D4C4B5]' : ''}`}>
                    <p className="font-medium text-[#2C1810]">{service.name}</p>
                    <div className="flex justify-between items-center text-sm mt-1">
                      <span className="text-[#5D4037]">{service.category}</span>
                      <span className="text-[#A67B5B]">{service.duration}</span>
                    </div>
                    <p className="text-[#C9A96E] text-sm">{service.price}</p>
                  </div>
                ))}
                {selectedBooking.totalDuration && (
                  <div className="pt-2 mt-2 border-t border-[#C9A96E]">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-[#2C1810]">Durée totale:</span>
                      <span className="text-sm font-semibold text-[#A67B5B]">{selectedBooking.totalDuration}</span>
                    </div>
                    {selectedBooking.discount > 0 && (
                      <>
                        <div className="flex justify-between items-center text-sm mt-1">
                          <span className="text-[#5D4037]">Sous-total:</span>
                          <span>{selectedBooking.subtotal?.toFixed(2)}€</span>
                        </div>
                        <div className="flex justify-between items-center text-sm text-[#C9A96E]">
                          <span>Réduction:</span>
                          <span>-{selectedBooking.discount?.toFixed(2)}€</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between items-center font-semibold mt-1">
                      <span className="text-[#2C1810]">Total:</span>
                      <span className="text-[#C9A96E]">{selectedBooking.totalPrice}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <CalendarIcon size={16} className="text-[#A67B5B]" />
                  <span>{selectedBooking.date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-[#A67B5B]" />
                  <span>{selectedBooking.time}</span>
                </div>
              </div>
              {selectedBooking.notes && (
                <div className="p-3 bg-[#F5F0EB] rounded-lg">
                  <p className="text-sm font-medium text-[#2C1810]">Notes :</p>
                  <p className="text-sm text-[#5D4037]">{selectedBooking.notes}</p>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <Select
                  value={selectedBooking.status}
                  onValueChange={(status) => {
                    updateBookingStatus(selectedBooking.id, status);
                    setSelectedBooking({ ...selectedBooking, status });
                  }}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="confirmed">Confirmé</SelectItem>
                    <SelectItem value="completed">Terminé</SelectItem>
                    <SelectItem value="cancelled">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="w-[95vw] sm:w-full max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le rendez-vous ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le rendez-vous sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteBooking(deleteDialog.booking?.id)}
              className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
